import { useCallback, useEffect, useRef, useState } from "react";
import { Rocket, Loader2, Hand, Repeat, ShieldAlert } from "lucide-react";
import { send, subscribe, forget } from "@/lib/deriv";
import { api } from "@/lib/api";
import { celebrateWin } from "@/lib/celebrate";
import { useAuth } from "@/hooks/useAuth";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getAdminToken, authHeader } from "@/lib/adminAuth";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";
import { RocketServerBot } from "./RocketServerBot";

// PROVEN pairs — work for ACCU on every Deriv account type.
const SYMBOLS = [
  { id: "1HZ10V", label: "⚡ Volatility 10 (1s)" },
  { id: "1HZ25V", label: "⚡ Volatility 25 (1s)" },
  { id: "1HZ50V", label: "⚡ Volatility 50 (1s)" },
  { id: "1HZ75V", label: "⚡ Volatility 75 (1s)" },
  { id: "1HZ100V", label: "⚡ Volatility 100 (1s)" },
  { id: "R_10", label: "Volatility 10" },
  { id: "R_25", label: "Volatility 25" },
  { id: "R_50", label: "Volatility 50" },
  { id: "R_75", label: "Volatility 75" },
  { id: "R_100", label: "Volatility 100" },
];
// Newer 1s pairs — availability varies by account/landing company, so they are
// PROBED through the user's live session and only shown if a real ACCU
// proposal succeeds on THEIR account. No dead pairs in the list, ever.
const PROBE_1S = [
  { id: "1HZ15V", label: "⚡ Volatility 15 (1s)" },
  { id: "1HZ30V", label: "⚡ Volatility 30 (1s)" },
  { id: "1HZ90V", label: "⚡ Volatility 90 (1s)" },
];
const ALL_KNOWN = [...SYMBOLS, ...PROBE_1S];
const GROWTH_RATES = [0.01, 0.02, 0.03, 0.04, 0.05];

const Rkt = ({ crashed, flying }) => (
  <svg viewBox="0 0 48 48" className={`w-11 h-11 transition-transform duration-300 ${crashed ? "rotate-[135deg]" : "rotate-45"}`} aria-label="rocket">
    <g>
      <path d="M24 4c6 5 9 12 9 20l-4 6h-10l-4-6c0-8 3-15 9-20z" fill={crashed ? "#ef4444" : "#e2e8f0"} />
      <circle cx="24" cy="18" r="4" fill="#0b1220" stroke={crashed ? "#fca5a5" : "#38bdf8"} strokeWidth="1.5" />
      <path d="M15 24l-6 8 8-2z" fill={crashed ? "#b91c1c" : "#38bdf8"} />
      <path d="M33 24l6 8-8-2z" fill={crashed ? "#b91c1c" : "#38bdf8"} />
      {!crashed && (
        <g className={flying ? "animate-pulse" : ""}>
          <path d="M20 30h8l-4 12z" fill="#f59e0b" />
          <path d="M22 30h4l-2 7z" fill="#fde047" />
        </g>
      )}
    </g>
  </svg>
);

const HeliRide = ({ authorized, currency = "USD", balance = 0, onConnect }) => {
  const { session } = useAuth();
  const [symbol, setSymbol] = useState("1HZ100V");
  const [growth, setGrowth] = useState(0.01);
  const [stake, setStake] = useState("1");
  const [autoCash, setAutoCash] = useState(true);
  const [targetX, setTargetX] = useState("1.25");
  const [phase, setPhase] = useState("idle"); // idle | buying | flying | cashed | crashed
  const [mult, setMult] = useState(1);
  const [ticks, setTicks] = useState(0);
  const [band, setBand] = useState(null);
  const [lastProfit, setLastProfit] = useState(null);
  const [curve, setCurve] = useState([]); // multiplier history for the live graph
  const [rounds, setRounds] = useState([]);
  const [autopilot, setAutopilot] = useState(false);
  const [apTP, setApTP] = useState("10");
  const [apSL, setApSL] = useState("15");
  const [apBanked, setApBanked] = useState(0); // total banked across auto-restarted sessions
  const [smartTP, setSmartTP] = useState(true); // 🎯 auto bank target by pair accuracy
  const [smartStake, setSmartStake] = useState(true); // 💰 stake = % of balance
  const [stakePct, setStakePct] = useState(() => {
    const v = parseFloat(localStorage.getItem("heliStakePct") || "10");
    return [5, 8, 10].includes(v) ? v : 10;
  });
  // SMART STAKE — chosen % of live balance (min $1), refreshed as the balance moves.
  const smartStakeVal = Math.min(2000, Math.max(1, Math.round(balance * (stakePct / 100) * 100) / 100));
  useEffect(() => {
    if (smartStake && balance > 0) setStake(String(smartStakeVal));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartStake, smartStakeVal]);
  const [scan, setScan] = useState(null);      // all-pair market scan, ranked best-first
  const [srvTrades, setSrvTrades] = useState([]); // 24/7 server bot flights — always in the log
  // 🧹 log-clear watermark: flights older than this stay hidden (persisted)
  const [logClearedAt, setLogClearedAt] = useState(() => Number(localStorage.getItem("heliLogClearedAt")) || 0);
  const [srvStat, setSrvStat] = useState({ net: 0, wins: 0, losses: 0, flights: 0, best: 0 }); // 24/7 run totals
  const [extra1s, setExtra1s] = useState([]);  // probed newer 1s pairs that THIS account can trade
  const symbols = [...SYMBOLS.slice(0, 5), ...extra1s, ...SYMBOLS.slice(5)];

  // PROBE — verify the newer 1s pairs with a REAL ACCU proposal on the user's
  // own session before offering them (availability differs per account).
  useEffect(() => {
    if (!authorized) return undefined;
    let stop = false;
    (async () => {
      const ok = [];
      for (const s of PROBE_1S) {
        try {
          const r = await send({ proposal: 1, amount: 5, basis: "stake", contract_type: "ACCU", currency: currency || "USD", underlying_symbol: s.id, growth_rate: 0.03 });
          if (!r?.error && r?.proposal?.id) ok.push(s);
        } catch { /* not tradable for this account — hide it */ }
      }
      if (!stop) setExtra1s(ok);
    })();
    return () => { stop = true; };
  }, [authorized, currency]);
  const flightRef = useRef(null); // { contractId, unsub, stake, settled }
  const apRef = useRef({ on: false, net: 0 });

  // PER-ACCOUNT session separation: demo and real rounds never mix. Rounds are
  // stored per account_id and swapped in/out when the header account changes.
  const acct = session?.account_id || "none";
  const acctRef = useRef(acct);
  useEffect(() => {
    if (acctRef.current === acct) return;
    acctRef.current = acct;
    try { flightRef.current?.unsub?.(); } catch { /* ignore */ }
    flightRef.current = null;
    setAutopilot(false);
    setPhase("idle"); setMult(1); setTicks(0); setBand(null); setLastProfit(null); setCurve([]);
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(`heliRounds:${acct}`) || "[]"); } catch { /* ignore */ }
    setRounds(Array.isArray(saved) ? saved : []);
    apRef.current.net = 0;
    apRef.current.banked = 0;
    setApBanked(0);
  }, [acct]);
  useEffect(() => {
    // hydrate on first mount too
    try {
      const saved = JSON.parse(localStorage.getItem(`heliRounds:${acct}`) || "[]");
      if (Array.isArray(saved) && saved.length) setRounds(saved);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try { localStorage.setItem(`heliRounds:${acctRef.current}`, JSON.stringify(rounds.slice(0, 15))); } catch { /* quota */ }
  }, [rounds]);

  const sessionNet = rounds.reduce((s, r) => s + r.profit, 0);
  const sessionNetRef = useRef(0);
  sessionNetRef.current = sessionNet;
  const wins = rounds.filter((r) => r.profit > 0).length;
  const bestTicks = rounds.reduce((m, r) => Math.max(m, r.ticks), 0);
  const flying = phase === "flying" || phase === "buying";

  // 🚨 DAILY CIRCUIT-BREAKER — 60% max daily loss across ALL flights
  // (local browser + 24/7 server). Tripped = grounded until the next UTC day.
  const [circuit, setCircuit] = useState(null); // {cap, net} when tripped
  const balanceRef = useRef(balance);
  balanceRef.current = balance;
  const checkCircuit = useCallback(async () => {
    const a = acctRef.current;
    if (!a || a === "none" || /^(VRTC|VRW|DOT)/i.test(a)) { setCircuit(null); return null; }
    try {
      const { data } = await api.get(`/rocket-bot/daily-guard/${a}`, { params: { balance: balanceRef.current || 0 } });
      setCircuit(data?.tripped ? data : null);
      return data;
    } catch { return null; }
  }, []);
  useEffect(() => { checkCircuit(); }, [acct, checkCircuit]);
  // 💳 while grounded, re-check every 45s — a fresh deposit raises the cap
  // server-side and automatically un-grounds the rocket.
  useEffect(() => {
    if (!circuit) return undefined;
    const iv = setInterval(() => { checkCircuit(); }, 45000);
    return () => clearInterval(iv);
  }, [circuit, checkCircuit]);

  // STOP FOR TODAY / PARK — lock the rocket to secure profits or cool down.
  const [dayLock, setDayLock] = useState(null); // {until, secured, mode}
  useEffect(() => {
    try {
      const l = JSON.parse(localStorage.getItem(`heliLock:${acct}`) || "null");
      setDayLock(l && l.until > Date.now() ? l : null);
    } catch { setDayLock(null); }
  }, [acct]);
  const lockUntil = (untilTs, mode) => {
    const l = { until: untilTs, secured: sessionNet, mode };
    try { localStorage.setItem(`heliLock:${acct}`, JSON.stringify(l)); } catch { /* quota */ }
    setDayLock(l);
    setAutopilot(false);
    // the lock is FINAL server-side too — parks the 24/7 rocket runner
    const hours = Math.max(1, Math.ceil((untilTs - Date.now()) / 3600000));
    api.post(`/rocket-bot/pause/${acct}`, mode === "day"
      ? { mode: "day", tz_offset_minutes: new Date().getTimezoneOffset() }
      : { mode: "hours", hours }).catch(() => {});
  };
  const secureDay = () => {
    if (!window.confirm("Park the rocket until tomorrow? This is FINAL — there is no early unlock.")) return;
    const until = new Date(); until.setHours(23, 59, 59, 999);
    lockUntil(until.getTime(), "day");
    toast.success(`🔒 Profits secured at +${sessionNet.toFixed(2)} ${currency} — rocket parked until tomorrow.`);
  };
  const parkHours = (h) => {
    if (!window.confirm(`Park the rocket for ${h} hour${h > 1 ? "s" : ""}? This is FINAL — there is no early unlock.`)) return;
    lockUntil(Date.now() + h * 3600 * 1000, "hours");
    toast.success(`🕐 Rocket parked for ${h} hours — smart cool-down.`);
  };
  // ADMIN-ONLY early unlock — clears the local park AND the server-side one.
  const adminUnlock = async () => {
    try {
      await api.post(`/admin/rocket-unpause/${acct}`, {}, { headers: authHeader() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Admin unlock failed — are you logged in as admin?");
      return;
    }
    try { localStorage.removeItem(`heliLock:${acct}`); } catch { /* ignore */ }
    setDayLock(null);
    toast.success("🔓 Admin unlock — rocket and 24/7 bot are free to fly again.");
  };
  // HARD LOCK — no early resume: once parked, the timer must run out.
  // A 1-minute ticker auto-unlocks when the period ends.
  useEffect(() => {
    if (!dayLock) return undefined;
    const id = setInterval(() => {
      if (dayLock.until <= Date.now()) {
        try { localStorage.removeItem(`heliLock:${acct}`); } catch { /* ignore */ }
        setDayLock(null);
        toast("🚀 Rest over — the rocket is unlocked. Fly smart!");
      }
    }, 60000);
    return () => clearInterval(id);
  }, [dayLock, acct]);

  const endFlight = useCallback((profit, ticksFinal, multFinal, why) => {
    const f = flightRef.current;
    if (!f || f.settled) return;
    f.settled = true;
    try { f.unsub?.(); } catch { /* ignore */ }
    const won = profit > 0;
    const peak = Math.max(f.peak || 1, won ? multFinal : 1); // highest x reached before the spike
    setPhase(won ? "cashed" : "crashed");
    setLastProfit(profit);
    setRounds((h) => [{ ticks: ticksFinal, mult: multFinal, peak, profit, why, at: Date.now() }, ...h].slice(0, 15));
    apRef.current.net += profit;
    toast[won ? "success" : "error"](
      won ? `🚀 Cashed out +${profit.toFixed(2)} ${currency} at ${multFinal.toFixed(2)}x`
          : `💥 Spiked after climbing to ${peak.toFixed(2)}x — lost ${Math.abs(profit).toFixed(2)} ${currency} (${ticksFinal} ticks)`);
    // advice note for the win popup — nudge smart behaviour, not greed
    const sNet = sessionNetRef.current + profit;
    const note = won
      ? (sNet > 0
        ? `You're +${sNet.toFixed(2)} ${currency} up this session — consider "Secure profits — stop for today" below to lock it in. 🔒`
        : `Nice catch — still ${sNet.toFixed(2)} ${currency} on the session. Set a bank target and stop when green.`)
      : "";
    if (won) celebrateWin({ engine: "🚀 Rocket Ride", amount: profit, currency, extra: `cashed out at ${multFinal.toFixed(2)}x after ${ticksFinal} ticks`, key: `heli:${f.contractId}`, contract: f.contractId, symbol: f.symbol, note });
    api.patch(`/track/trade/${f.contractId}`, { status: won ? "won" : "lost", profit })
      .then(() => { if (!won) checkCircuit().then((g) => { if (g?.tripped) { setAutopilot(false); toast.error("🚨 Daily circuit-breaker hit — down 60% today across all flights. The rocket is grounded until the next UTC day."); } }); })
      .catch(() => {});
    flightRef.current = null;
    // NO streak-guard stop (user choice): win or lose, autopilot keeps flying —
    // only the stop-loss (or a manual park) ends the run.
  }, [currency, checkCircuit]);

  const fly = useCallback(async () => {
    if (dayLock) { toast("🔒 The rocket is parked — it unlocks automatically when your rest period ends."); return; }
    if (circuit) { setAutopilot(false); toast.error("🚨 Daily circuit-breaker — the rocket is grounded until the next UTC day to protect your balance."); return; }
    const stk = Math.max(1, parseFloat(stake) || 1);
    setPhase("buying");
    setMult(1); setTicks(0); setBand(null); setLastProfit(null); setCurve([1]);
    try {
      const req = {
        proposal: 1, subscribe: 1, amount: stk, basis: "stake", contract_type: "ACCU",
        currency, underlying_symbol: symbol, growth_rate: growth,
      };
      const tx = parseFloat(targetX);
      let tpAmt = 0;
      if (apRef.current.on) {
        // AUTOPILOT: EVERY flight carries a HARD Deriv-side cash-out at the
        // rolled 1.1–1.3x of THIS stake — past losses can never push a single
        // flight beyond 1.3x (the 2.81x runaway bug).
        const f = apRef.current.factor > 0 ? apRef.current.factor : 0.2;
        tpAmt = Math.max(0.02, Math.round(stk * f * 100) / 100);
      } else if (autoCash && tx > 1) {
        tpAmt = Math.round(stk * (tx - 1) * 100) / 100;
      }
      if (tpAmt > 0) req.limit_order = { take_profit: tpAmt };
      const p = await send(req);
      if (p?.error) throw new Error(p.error.message || "Deriv rejected the proposal");
      const buy = await send({ buy: p.proposal.id, price: p.proposal.ask_price });
      if (buy?.error) throw new Error(buy.error.message || "Buy failed");
      const contractId = buy.buy?.contract_id;
      setPhase("flying");
      api.post("/track/trade", {
        account_id: session?.account_id || "unknown", contract_id: String(contractId),
        symbol, contract_type: "ACCU", stake: parseFloat(buy.buy?.buy_price || stk),
        payout: stk, currency, strategy: "Rocket Ride", source: "heli_ride",
      }).catch(() => {});
      const flight = { contractId, symbol, unsub: null, settled: false, selling: false, peak: 1, tpAmt };
      flightRef.current = flight;
      const { subscription_id } = await subscribe(
        { proposal_open_contract: 1, contract_id: contractId },
        (msg) => {
          const c = msg?.proposal_open_contract;
          if (!c || flightRef.current !== flight || flight.settled) return;
          const bid = parseFloat(c.bid_price || 0);
          const buyPx = parseFloat(c.buy_price || stk);
          const m = buyPx > 0 && bid > 0 ? bid / buyPx : 1;
          // REAL ticks survived — c.tick_count on ACCU is the contract's MAX
          // tick life (a constant, e.g. 85), not progress. Prefer tick_passed,
          // else count spot updates ourselves.
          const spotT = Number(c.current_spot_time || 0);
          if (spotT && spotT !== flight.lastSpotT) {
            flight.lastSpotT = spotT;
            flight.tkCount = (flight.tkCount || 0) + 1;
          }
          const tk = Number(c.tick_passed ?? 0) || flight.tkCount || 0;
          if (bid > 0) { if (m > flight.peak) flight.peak = m; setMult(m); setCurve((c) => (c.length >= 300 ? [...c.slice(-299), m] : [...c, m])); }
          if (tk) setTicks(tk);
          if (c.high_barrier && c.low_barrier) setBand({ hi: c.high_barrier, lo: c.low_barrier });
          // AUTOPILOT MID-FLIGHT BANK: the instant the session (banked so far +
          // live profit) reaches the bank target, cash out NOW — don't wait for
          // the auto cash-out multiplier.
          const livePf = bid > 0 ? bid - buyPx : 0;
          // FAILSAFE CASH-OUT: if Deriv's take-profit limit order hasn't fired
          // by the time live profit reaches this flight's target, sell now —
          // a flight must never ride past its rolled 1.1–1.3x exit.
          if (!flight.selling && flight.tpAmt > 0 && livePf >= flight.tpAmt && (c.is_valid_to_sell ?? true)) {
            flight.selling = true;
            send({ sell: contractId, price: 0 }).catch(() => { flight.selling = false; });
          }
          if (apRef.current.on && !flight.selling && livePf > 0 && apRef.current.tp > 0
              && apRef.current.net + livePf >= apRef.current.tp && (c.is_valid_to_sell ?? true)) {
            flight.selling = true;
            send({ sell: contractId, price: 0 }).catch(() => { flight.selling = false; });
          }
          if (c.is_sold || ["sold", "won", "lost"].includes(c.status)) {
            endFlight(Number(c.profit ?? 0), tk || 0, m, c.status);
          }
        });
      flight.unsub = () => forget(subscription_id);
    } catch (e) {
      setPhase("idle");
      flightRef.current = null;
      toast.error("Couldn't take off", { description: e?.message || String(e) });
    }
  }, [stake, currency, symbol, growth, autoCash, targetX, session, endFlight, dayLock, circuit]);

  const cashOut = useCallback(async () => {
    const f = flightRef.current;
    if (!f || f.settled) return;
    try {
      const res = await send({ sell: f.contractId, price: 0 });
      if (res?.error) throw new Error(res.error.message);
    } catch (e) {
      toast.error("Cash out failed", { description: e?.message || String(e) });
    }
  }, []);

  // AUTOPILOT: repeat flights round after round. Hitting the bank target
  // BANKS the profit and RESTARTS a fresh session automatically — only the
  // stop-loss (or the toggle) ends the run.
  useEffect(() => {
    apRef.current.on = autopilot;
    apRef.current.tp = parseFloat(apTP) || 0;
    if (!autopilot) return undefined;
    const id = setInterval(() => {
      if (!apRef.current.on || flightRef.current) return;
      const net = apRef.current.net;
      const tp = parseFloat(apTP) || 0;
      const sl = parseFloat(apSL) || 0;
      if (sl > 0 && net <= -sl) {
        setAutopilot(false);
        toast.error(`🛑 Autopilot stopped at the loss cap (${net.toFixed(2)} ${currency}).`);
        return;
      }
      if (tp > 0 && net >= tp) {
        apRef.current.banked = (apRef.current.banked || 0) + net;
        apRef.current.net = 0;
        setApBanked(apRef.current.banked);
        toast.success(`🏦 Banked +${net.toFixed(2)} ${currency} — autopilot restarting (total banked +${apRef.current.banked.toFixed(2)})`);
        celebrateWin({ engine: "🚀 Rocket Autopilot", amount: net, currency, extra: `session target banked · total +${apRef.current.banked.toFixed(2)} ${currency}`, key: `apbank:${Date.now()}`, note: "Autopilot restarts automatically — your stop-loss is the only exit, so keep it sized right. Park anytime with Secure Profits." });
        return; // fresh session starts on the next interval tick
      }
      // AUTOPILOT SMART PAIR: always fly the ⭐ best pair from the scanner
      const best = bestRef.current;
      if (best && best !== symbolRef.current) {
        setSymbol(best);
        toast(`⭐ Autopilot switched to ${best} — best pair right now`);
        return; // launch on the fresh pair next tick
      }
      // 🟢 GREEN-ONLY GATE (user rule): launch ONLY when the best pair reads
      // CALM (ratio ≤ 0.9). 🟡 NORMAL / 🔴 SPIKY skies are skipped — that's
      // where spikes hide. Re-checked every 2.5s; lifts off the moment it's green.
      const row = bestRowRef.current;
      if (!row || row.ratio > 0.9) {
        setApWaitingCalm(true);
        return;
      }
      setApWaitingCalm(false);
      fly();
    }, 2500);
    return () => clearInterval(id);
  }, [autopilot, apTP, apSL, fly, currency]);

  const bestRef = useRef(null);
  const bestRowRef = useRef(null);
  const [apWaitingCalm, setApWaitingCalm] = useState(false);
  const symbolRef = useRef(symbol);
  symbolRef.current = symbol;

  useEffect(() => () => { try { flightRef.current?.unsub?.(); } catch { /* ignore */ } }, []);

  // MARKET SCANNER — every 30s reads the last 300 live ticks of EVERY pair,
  // measures calmness (recent movement vs its own normal) + chop (how often
  // the direction flips), and ranks them: calm + choppy = longest ACCU flights.
  useEffect(() => {
    if (!authorized) return undefined;
    let stop = false;
    const scanOne = async (id) => {
      const r = await send({ ticks_history: id, count: 300, end: "latest", style: "ticks" });
      const prices = ((r?.history?.prices) || []).map(Number);
      const times = (r?.history?.times || []).map(Number);
      if (prices.length < 100) return null;
      const rets = prices.slice(1).map((p, i) => Math.abs(p - prices[i]) / prices[i]);
      const avg = (a) => a.reduce((s, x) => s + x, 0) / a.length;
      const ratio = avg(rets.slice(0, rets.length - 50)) > 0 ? avg(rets.slice(-50)) / avg(rets.slice(0, rets.length - 50)) : 1;
      const span = times.length > 1 ? times[times.length - 1] - times[0] : 0;
      const tps = span > 0 ? (times.length - 1) / span : 0;
      const tail = prices.slice(-21);
      const dirs = tail.slice(1).map((p, i) => Math.sign(p - tail[i]));
      const flips = dirs.slice(1).filter((d, i) => d !== 0 && dirs[i] !== 0 && d !== dirs[i]).length;
      const chop = Math.round((flips / Math.max(1, dirs.length - 1)) * 100);
      const tape = tail.slice(1).map((p, i) => ({ up: p > tail[i], flat: p === tail[i] }));
      return { id, ratio, tps, chop, tape };
    };
    const run = async () => {
      const out = [];
      for (const s of [...SYMBOLS, ...extra1s]) {
        if (stop) return;
        try { const row = await scanOne(s.id); if (row) out.push(row); } catch { /* skip pair */ }
      }
      if (stop || !out.length) return;
      out.sort((a, b) => (a.ratio - a.chop / 400) - (b.ratio - b.chop / 400));
      setScan(out);
    };
    run();
    const iv = setInterval(run, 30000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, extra1s.length]);
  // if the account can't trade the selected probed pair, snap back to a proven one
  useEffect(() => {
    if (!symbols.some((s) => s.id === symbol)) setSymbol("1HZ100V");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extra1s.length]);
  const read = scan?.find((s) => s.id === symbol) || null;
  const bestPair = scan?.[0] || null;
  bestRef.current = bestPair?.id || null;
  bestRowRef.current = bestPair;
  // SMART TARGET — bank between $3 and $10 based on the best pair's accuracy
  // (calmness + chop): confident conditions earn a bigger target, rough
  // conditions bank early.
  const pairAccuracy = bestPair
    ? Math.round((0.6 * Math.min(1, Math.max(0, (1.25 - bestPair.ratio) / 0.5)) + 0.4 * Math.min(1, Math.max(0, (bestPair.chop - 30) / 40))) * 100)
    : null;
  // target = stake × 0.1–0.3 profit, RE-ROLLED after every flight so it banks
  // anywhere between 1.1x and 1.3x (never glued to the top — spikes love 1.2x).
  // Pair accuracy sets the ceiling; the roll picks the exact exit.
  const [tgtRoll, setTgtRoll] = useState(() => Math.random());
  useEffect(() => { setTgtRoll(Math.random()); }, [rounds.length]);
  const maxFactor = 0.05 + ((pairAccuracy ?? 50) / 100) * 0.2;
  const smartFactor = 0.05 + tgtRoll * (maxFactor - 0.05);
  // autopilot flights read the CURRENT rolled factor for their hard per-flight cash-out
  useEffect(() => { apRef.current.factor = smartFactor; }, [smartFactor]);
  const smartTarget = Math.min(100, Math.max(0.1, Math.round(((parseFloat(stake) || 1) * smartFactor) * 100) / 100));
  useEffect(() => {
    if (smartTP) setApTP(String(smartTarget));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartTP, smartTarget]);
  const labelOf = (id) => ALL_KNOWN.find((s) => s.id === id)?.label || id;

  if (!authorized) {
    return (
      <div data-testid="heli-connect-prompt" className="vts-card p-8 text-center space-y-3">
        <div className="flex justify-center"><Rkt /></div>
        <h3 className="font-heading font-bold text-lg text-white">Rocket Ride — Accumulators</h3>
        <p className="text-sm text-vts-silver/70 max-w-sm mx-auto">
          Your stake grows up to 5% every tick while the rocket stays in the safe zone — cash out any time.
          Log in with Deriv to launch (works with your free $10,000 demo account).
        </p>
        <button data-testid="heli-connect-btn" onClick={onConnect}
          className="rounded-lg bg-vts-blue px-6 py-2.5 text-sm font-bold text-white hover:bg-vts-blue/90">
          Login with Deriv
        </button>
        <p className="text-[11px] text-vts-silver/60">
          New to Deriv?{" "}
          <a data-testid="heli-connect-affiliate-link" href={DERIV_AFFILIATE_URL} target="_blank" rel="noreferrer"
            className="text-vts-blue hover:text-white underline underline-offset-2 transition">
            Create a free account →
          </a>
        </p>
      </div>
    );
  }
  const stakeNum = parseFloat(stake) || 1;
  const liveProfit = Math.max(0, (mult - 1) * stakeNum);
  // AVIATOR-STYLE GRAPH — multiplier curve with axis labels + rocket at the tip
  const W = 320, H = 168, PADL = 40, PADT = 14, PADB = 20, PADR = 26;
  const idleCurve = Array.from({ length: 40 }, (_, i) => 1 + 0.5 * Math.pow(i / 39, 1.9));
  const pts = curve.length > 1 ? curve : idleCurve;
  const isLive = curve.length > 1;
  const tgtX = parseFloat(targetX) || 1.5;
  const maxM = Math.max(1.1, ...(isLive ? [mult * 1.12, autoCash ? tgtX * 1.04 : 0] : [1.55]));
  const gy = (m) => PADT + (H - PADT - PADB) * (1 - Math.min(1, (m - 1) / (maxM - 1)));
  // time flows LEFT → RIGHT: the width only fills up after ~60 ticks, so the
  // rocket launches from the bottom-left and travels right (Aviator-style)
  const denom = Math.max(pts.length - 1, 59);
  const gx = (i) => PADL + (W - PADL - PADR) * (i / denom);
  const linePath = pts.map((m, i) => `${i ? "L" : "M"}${gx(i).toFixed(1)},${gy(m).toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${gx(pts.length - 1).toFixed(1)},${H - PADB} L${PADL},${H - PADB} Z`;
  const gridVals = [0, 0.25, 0.5, 0.75, 1].map((f) => 1 + f * (maxM - 1));
  const tipX = isLive ? gx(pts.length - 1) : PADL;
  const tipY = isLive ? gy(pts[pts.length - 1]) : gy(1);
  const curveColor = phase === "crashed" ? "#ef4444" : phase === "cashed" ? "#f59e0b" : "#34d399";

  return (
    <div data-testid="heli-ride-panel" className="space-y-3">
      {/* AVIATOR-STYLE FLIGHT GRAPH */}
      <div className="relative overflow-hidden rounded-xl border border-vts-line bg-gradient-to-b from-[#0a1a33] via-[#0b1220] to-[#05080f]">
        <svg data-testid="heli-flight-graph" viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "230px" }} preserveAspectRatio="none">
          {gridVals.map((v) => (
            <line key={v} x1={PADL} y1={gy(v)} x2={W - 6} y2={gy(v)} stroke="#1e3a5f" strokeWidth="0.6" strokeDasharray="4 5" opacity="0.55" />
          ))}
          {isLive && autoCash && tgtX > 1 && tgtX <= maxM && (
            <line x1={PADL} y1={gy(tgtX)} x2={W - 6} y2={gy(tgtX)} stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="6 4" opacity="0.8" />
          )}
          <path d={areaPath} fill={curveColor} opacity={isLive ? 0.14 : 0.05} />
          <path d={linePath} fill="none" stroke={curveColor} strokeWidth={isLive ? 2.4 : 1.2} strokeLinecap="round" opacity={isLive ? 1 : 0.35} />
          {isLive && <circle cx={tipX} cy={tipY} r="3.2" fill={curveColor} />}
        </svg>
        {/* axis labels as HTML — immune to the svg stretch */}
        {gridVals.map((v) => (
          <div key={v} className="absolute font-mono text-[10px] text-sky-300/75 pointer-events-none"
            style={{ left: 6, top: `calc(${(gy(v) / H) * 100}% - 7px)` }}>
            {v >= 2 ? `${v.toFixed(1)}x` : `${v.toFixed(2)}x`}
          </div>
        ))}
        {isLive && autoCash && tgtX > 1 && tgtX <= maxM && (
          <div className="absolute font-mono text-[9px] text-amber-300 pointer-events-none"
            style={{ right: 8, top: `calc(${(gy(tgtX) / H) * 100}% - 14px)` }}>🎯 {tgtX}x</div>
        )}
        {/* rocket rides the curve tip */}
        <div className="absolute transition-all duration-300 ease-out pointer-events-none"
          style={{ left: `calc(${(tipX / W) * 100}% - 22px)`, top: `calc(${(tipY / H) * 100}% - 40px)` }}>
          <Rkt crashed={phase === "crashed"} flying={flying} />
        </div>
        {/* live multiplier + profit readout — centered so the rocket never hides it */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 text-center pointer-events-none">
          <div data-testid="heli-multiplier" className={`font-mono text-4xl font-bold tabular-nums ${
            phase === "crashed" ? "text-vts-loss" : phase === "cashed" ? "text-amber-300" : "text-vts-profit"}`}>
            {mult.toFixed(2)}x
          </div>
          {flying && (
            <div data-testid="heli-live-profit" className="font-mono text-lg font-bold text-vts-profit tabular-nums">
              +{liveProfit.toFixed(2)} {currency}
            </div>
          )}
          <div className="text-[10px] font-mono text-vts-silver/60">
            {phase === "flying" && `🚀 in flight · ${ticks} ticks`}
            {phase === "cashed" && lastProfit != null && `banked +${lastProfit.toFixed(2)} ${currency}`}
            {phase === "crashed" && lastProfit != null && `lost ${Math.abs(lastProfit).toFixed(2)} ${currency}`}
            {(phase === "idle" || phase === "buying") && "ready for launch"}
          </div>
        </div>
        {band && phase === "flying" && (
          <div className="absolute bottom-2 left-4 text-[9px] font-mono text-vts-silver/50">
            safe zone: {band.lo} — {band.hi}
          </div>
        )}
      </div>

      {/* CONTROLS */}
      <div className="vts-card p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Market</label>
            <select data-testid="heli-symbol-select" value={symbol} disabled={flying}
              onChange={(e) => setSymbol(e.target.value)}
              className="w-full mt-0.5 rounded-lg border border-vts-line bg-vts-surface px-2.5 py-2 text-xs text-white font-mono outline-none">
              {symbols.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Stake ({currency})</label>
            <input data-testid="heli-stake-input" type="number" min="1" step="1" value={stake} disabled={flying || (smartStake && balance > 0)}
              onChange={(e) => setStake(e.target.value)}
              className="w-full mt-0.5 rounded-lg border border-vts-line bg-vts-surface px-2.5 py-2 text-xs text-white font-mono outline-none disabled:opacity-60" />
            <button data-testid="heli-smart-stake-toggle" onClick={() => setSmartStake((v) => !v)}
              className={`mt-1 w-full rounded-md border px-2 py-1 text-[9px] font-bold font-mono transition ${
                smartStake ? "border-vts-profit/50 bg-vts-profit/10 text-vts-profit" : "border-vts-line bg-vts-surface text-vts-silver/55 hover:text-white"}`}>
              💰 {stakePct}% OF BALANCE {smartStake ? (balance > 0 ? `· $${smartStakeVal}` : "· waiting for balance") : "OFF"}
            </button>
            {smartStake && (
              <div className="mt-1 flex gap-1">
                {[5, 8, 10].map((p) => (
                  <button key={p} data-testid={`heli-stake-pct-${p}`} disabled={flying}
                    onClick={() => { setStakePct(p); try { localStorage.setItem("heliStakePct", String(p)); } catch { /* quota */ } }}
                    className={`flex-1 rounded-md border px-1 py-0.5 text-[9px] font-bold font-mono transition ${
                      stakePct === p ? "border-vts-blue bg-vts-blue/20 text-vts-blue" : "border-vts-line bg-vts-surface text-vts-silver/55 hover:text-white"}`}>
                    {p}%
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Growth per tick (higher = faster + riskier)</label>
          <div className="flex gap-1.5 mt-1">
            {GROWTH_RATES.map((g) => (
              <button key={g} data-testid={`heli-growth-${g * 100}`} disabled={flying}
                onClick={() => setGrowth(g)}
                className={`flex-1 rounded-lg py-2 text-xs font-mono font-bold border transition ${
                  growth === g ? "bg-vts-blue text-white border-vts-blue" : "bg-vts-surface text-vts-silver/70 border-vts-line hover:text-white"}`}>
                {Math.round(g * 100)}%
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch data-testid="heli-autocash-toggle" checked={autoCash} onCheckedChange={setAutoCash} disabled={flying} />
            <span className="text-[11px] text-vts-silver/70 font-mono">Auto cash-out at</span>
          </div>
          <input data-testid="heli-target-input" type="number" min="1.05" step="0.05" value={targetX}
            disabled={!autoCash || flying} onChange={(e) => setTargetX(e.target.value)}
            className="w-20 rounded-lg border border-vts-line bg-vts-surface px-2 py-1.5 text-xs text-white font-mono outline-none disabled:opacity-40" />
          <span className="text-[11px] text-vts-silver/50 font-mono">x</span>
        </div>
        {flying ? (
          <button data-testid="heli-cashout-btn" onClick={cashOut} disabled={phase === "buying"}
            className="w-full h-14 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-heading font-extrabold text-lg tracking-wide transition disabled:opacity-60 flex items-center justify-center gap-2">
            {phase === "buying" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Hand className="h-5 w-5" />}
            {phase === "buying" ? "TAKING OFF…" : `CASH OUT ${(Math.max(0, (mult - 1)) * (parseFloat(stake) || 1)).toFixed(2)} ${currency}`}
          </button>
        ) : (
          <button data-testid="heli-fly-btn" onClick={fly} disabled={autopilot || !!dayLock}
            className="w-full h-14 rounded-xl bg-gradient-to-r from-sky-500 to-vts-blue hover:from-sky-400 hover:to-vts-blue/90 text-white font-heading font-extrabold text-lg tracking-widest transition flex items-center justify-center gap-2 disabled:opacity-50">
            <Rocket className="h-5 w-5" /> {dayLock ? (dayLock.mode === "hours" ? "PARKED — COOLING DOWN" : "PARKED FOR TODAY") : "LIFT OFF"}
          </button>
        )}
      </div>

      {/* 🚨 DAILY CIRCUIT-BREAKER — grounded until the next UTC day */}
      {circuit && (
        <div data-testid="heli-circuit-banner" className="rounded-xl border border-vts-loss/50 bg-vts-loss/10 p-4 flex items-center gap-3 flex-wrap">
          <span className="text-2xl">🚨</span>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-bold text-vts-loss">Daily circuit-breaker — down {Number(circuit.net).toFixed(2)} {currency} today across ALL flights (cap −{Number(circuit.cap).toFixed(2)}).</div>
            <div className="text-[10px] text-vts-silver/60 font-mono">
              {circuit.trips >= 2
                ? "Second session lost too — grounded until the next UTC day. Local + 24/7 server flights count together."
                : circuit.rest_until
                  ? `⏰ 5-hour cool-down until ${String(circuit.rest_until).slice(11, 16)} UTC — then the rocket auto-resumes with a smaller fresh allowance (30% of what's left).`
                  : "Local + 24/7 server flights count together."}{" "}
              💳 Deposit fresh funds and the breaker automatically gives you new room (checked every 45s).
            </div>
          </div>
          {!!getAdminToken() && (
            <button data-testid="heli-circuit-override" onClick={async () => {
              try {
                await api.post(`/admin/rocket-guard-override/${acct}`, {}, { headers: authHeader() });
                setCircuit(null);
                toast.success("🔓 Admin override — circuit-breaker lifted for today.");
              } catch (e) { toast.error(e?.response?.data?.detail || "Override failed — are you logged in as admin?"); }
            }}
              className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20 shrink-0">
              🔓 Admin override
            </button>
          )}
        </div>
      )}

      {/* SECURE PROFITS — stop for the day */}
      {dayLock ? (
        <div data-testid="heli-daylock-banner" className="rounded-xl border border-vts-profit/40 bg-vts-profit/10 p-4 flex items-center gap-3 flex-wrap">
          <span className="text-2xl">🔒</span>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-bold text-vts-profit">Profits secured — rocket parked{dayLock.secured > 0 ? ` at +${dayLock.secured.toFixed(2)} ${currency}` : ""}.</div>
            <div className="text-[10px] text-vts-silver/60 font-mono">
              {dayLock.mode === "hours"
                ? `Cool-down until ${new Date(dayLock.until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} — the sky will look different by then.`
                : "Smart move — banked profit today beats a revenge flight. The rocket unlocks at midnight."}
            </div>
          </div>
          {!!getAdminToken() && (
            <button data-testid="heli-admin-unlock" onClick={adminUnlock}
              className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20 shrink-0">
              🔓 Admin unlock
            </button>
          )}
        </div>
      ) : (sessionNet > 0 && !flying && (
        <div data-testid="heli-secure-prompt" className="rounded-xl border border-amber-500/35 bg-amber-500/10 p-4 flex items-center gap-3 flex-wrap">
          <span className="text-2xl">💰</span>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-bold text-amber-300">You're up +{sessionNet.toFixed(2)} {currency} this session — want to secure it?</div>
            <div className="text-[10px] text-vts-silver/60 font-mono">Stopping while in profit is how winners stay winners. Locks are final — the rocket stays parked until tomorrow.</div>
          </div>
          <button data-testid="heli-secure-btn" onClick={secureDay}
            className="rounded-lg bg-vts-profit px-3 py-2 text-[11px] font-extrabold text-black hover:bg-vts-profit/85 shrink-0">
            🔒 Secure profits — stop for today
          </button>
        </div>
      ))}

      {/* REST — park the rocket for a chosen period, any time */}
      {!dayLock && (
        <div data-testid="heli-rest-row" className="rounded-xl border border-vts-line/40 bg-vts-surface/30 px-3 py-2.5 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-mono text-vts-silver/60">😴 <b className="text-white">Need a rest?</b> Park the rocket — the lock is final until the time is up:</span>
          {[1, 3, 5].map((h) => (
            <button key={h} data-testid={`heli-rest-${h}h`} onClick={() => parkHours(h)} disabled={flying}
              className="rounded-md border border-vts-line bg-vts-surface px-3 py-1.5 text-[11px] font-bold text-vts-silver/80 hover:text-white hover:border-vts-blue/50 disabled:opacity-40">
              {h}h
            </button>
          ))}
          <button data-testid="heli-rest-today" onClick={secureDay} disabled={flying}
            className="rounded-md border border-vts-line bg-vts-surface px-3 py-1.5 text-[11px] font-bold text-vts-silver/80 hover:text-white hover:border-vts-blue/50 disabled:opacity-40">
            Until tomorrow
          </button>
        </div>
      )}

      {/* AUTOPILOT */}
      <div data-testid="heli-autopilot-card" className="vts-card p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-vts-blue" />
          <span className="text-sm font-semibold text-white">Autopilot</span>
          <span className="text-[10px] text-vts-silver/55 font-mono">every flight hard-cashes at its rolled 1.05–1.25x, banks the session target mid-flight, flies the ⭐ best pair, restarts itself — only the stop-loss ends it</span>
          {apBanked > 0 && (
            <span data-testid="heli-ap-banked" className="px-1.5 py-0.5 rounded bg-vts-profit/15 text-vts-profit text-[10px] font-bold font-mono">
              🏦 banked +{apBanked.toFixed(2)}
            </span>
          )}
          <div className="ml-auto">
            <Switch data-testid="heli-autopilot-toggle" checked={autopilot} disabled={!!dayLock || !!circuit}
              onCheckedChange={(v) => { if (v) { apRef.current.net = 0; apRef.current.banked = 0; setApBanked(0); } setAutopilot(v); }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Bank &amp; restart at +{currency}</label>
            <input data-testid="heli-ap-tp" type="number" min="1" value={apTP} onChange={(e) => setApTP(e.target.value)} disabled={autopilot || smartTP}
              className="w-full mt-0.5 rounded-lg border border-vts-line bg-vts-surface px-2.5 py-1.5 text-xs text-white font-mono outline-none disabled:opacity-60" />
            <button data-testid="heli-smart-tp-toggle" onClick={() => setSmartTP((v) => !v)}
              className={`mt-1 w-full rounded-md border px-2 py-1 text-[9px] font-bold font-mono transition ${
                smartTP ? "border-vts-blue/50 bg-vts-blue/15 text-vts-blue" : "border-vts-line bg-vts-surface text-vts-silver/55 hover:text-white"}`}>
              🎯 SMART {smartTP ? "ON" : "OFF"}{smartTP && pairAccuracy != null ? ` · $${smartTarget} ≈ ${(1 + smartFactor).toFixed(2)}x — re-rolled every flight` : " · banks between 1.05–1.25x, varies every flight"}
            </button>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Stop at −{currency}</label>
            <input data-testid="heli-ap-sl" type="number" min="1" value={apSL} onChange={(e) => setApSL(e.target.value)} disabled={autopilot}
              className="w-full mt-0.5 rounded-lg border border-vts-line bg-vts-surface px-2.5 py-1.5 text-xs text-white font-mono outline-none" />
          </div>
        </div>
        {autopilot && apWaitingCalm && (
          <div data-testid="heli-ap-waiting-calm" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[10px] font-mono text-amber-300">
            ⏸ Holding launches — skies are 🟡/🔴 right now. Autopilot only lifts off on 🟢 CALM (fewer spikes). Scanning every pair — it flies the moment one turns green.
          </div>
        )}
        <p className="text-[9px] text-vts-silver/45 font-mono flex items-center gap-1">
          <ShieldAlert className="h-3 w-3" /> Launches ONLY on 🟢 CALM skies (🟡 normal and 🔴 spiky are skipped — that's where spikes hide), banks the target mid-flight, re-checks the ⭐ trending pair before every launch, restarts automatically. 🎯 Smart target banks between 1.05–1.25x of your stake — the exact exit is re-rolled after every flight (never glued to one number), max $100.
        </p>
      </div>

      {/* 24/7 SERVER MODE — the rocket keeps flying with the browser closed */}
      <RocketServerBot authorized={authorized} acct={acct} token={session?.token}
        currency={currency} locked={!!dayLock || !!circuit}
        onStatus={(d) => {
          const t = Array.isArray(d?.recent_trades) ? d.recent_trades : [];
          // only re-render when the trade list actually changed — keeps the page still
          setSrvTrades((prev) => (prev.length === t.length && prev[0]?.contract_id === t[0]?.contract_id ? prev : t));
          const s = { net: Number(d?.net || 0), wins: Number(d?.wins || 0), losses: Number(d?.losses || 0),
                      flights: Number(d?.flights || 0), best: Number(d?.best_ticks || 0) };
          setSrvStat((prev) => (prev.net === s.net && prev.flights === s.flights && prev.wins === s.wins && prev.best === s.best ? prev : s));
        }} />

      {/* SESSION STATS — per-account, local flights + 🛰️ 24/7 server run */}
      <div className="flex items-center gap-2 px-1">
        <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Session stats — local + 🛰️ 24/7 server</span>
        <span data-testid="heli-session-account" className={`px-1.5 py-0.5 rounded text-[9px] font-bold font-mono ${
          /^(VRTC|VRW|DOT)/i.test(acct) ? "bg-amber-500/15 text-amber-300" : "bg-vts-profit/15 text-vts-profit"}`}>
          {/^(VRTC|VRW|DOT)/i.test(acct) ? "DEMO" : "REAL"} · {acct}
        </span>
      </div>
      {(() => {
        const combNet = sessionNet + srvStat.net;
        const combRounds = rounds.length + srvStat.flights;
        const combWins = wins + srvStat.wins;
        const combBest = Math.max(bestTicks || 0, srvStat.best || 0);
        return (
          <div className="grid grid-cols-4 gap-2">
            {[["Session P/L", `${combNet >= 0 ? "+" : ""}${combNet.toFixed(2)}`, combNet >= 0 ? "text-vts-profit" : "text-vts-loss"],
              ["Win rate", combRounds ? `${Math.round((combWins / combRounds) * 100)}%` : "—", "text-white"],
              ["Rounds", `${combRounds}`, "text-white"],
              ["Best run", combBest ? `${combBest}t` : "—", "text-amber-300"]].map(([l, v, c]) => (
              <div key={l} data-testid={`heli-stat-${l.replace(/\s|\//g, "-").toLowerCase()}`} className="vts-card p-3 text-center">
                <div className={`font-mono text-base font-bold tabular-nums ${c}`}>{v}</div>
                <div className="text-[9px] uppercase tracking-wider text-vts-silver/50 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* FLIGHT LOG — local flights + 🛰️ 24/7 server flights, always together */}
      {(() => {
        const merged = [
          ...rounds.map((r) => ({ ...r, src: "local", k: `l${r.at}` })),
          ...srvTrades.map((t) => ({
            ticks: t.ticks, mult: Number(t.mult || 1), peak: Number(t.peak_mult ?? t.mult ?? 1),
            profit: Number(t.profit || 0), at: Date.parse(t.created_at) || 0, src: "server", k: `s${t.contract_id}`,
            pair: t.symbol ? String(t.symbol).replace(/^1HZ(\d+)V$/, "V$1·1s").replace(/^R_(\d+)$/, "V$1") : "",
          })),
        ].sort((a, b) => b.at - a.at).filter((r) => r.at > logClearedAt).slice(0, 20);
        if (!merged.length) return null;
        return (
          <div data-testid="heli-round-history" className="vts-card p-4">
            <div className="flex items-center mb-2">
              <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold">
                Flight log <span className="normal-case text-vts-silver/40">— manual + autopilot + 🛰️ 24/7 server</span>
              </div>
              <button
                data-testid="heli-clear-log"
                onClick={() => {
                  const now = Date.now();
                  setLogClearedAt(now);
                  setRounds([]);
                  try { localStorage.setItem("heliLogClearedAt", String(now)); } catch { /* quota */ }
                }}
                className="ml-auto text-[9px] font-mono uppercase tracking-wider text-vts-silver/50 hover:text-white border border-vts-line rounded px-2 py-1 transition"
              >
                🧹 Clear log
              </button>
            </div>
            <div className="space-y-1">
              {merged.map((r) => (
                <div key={r.k} className={`flex items-center gap-2 text-xs font-mono rounded-lg px-2.5 py-1.5 ${
                  r.profit > 0 ? "bg-vts-profit/10 text-vts-profit" : "bg-vts-loss/10 text-vts-loss"}`}>
                  <span>{r.profit > 0 ? "✓" : "✕"}</span>
                  {r.src === "server" && <span data-testid="flight-log-server-tag" className="px-1 py-0.5 rounded bg-vts-blue/15 text-vts-blue text-[9px] font-bold">🛰️ 24/7</span>}
                  {r.pair && <span className="px-1 py-0.5 rounded bg-vts-surface text-vts-silver/60 text-[9px] font-bold">{r.pair}</span>}
                  <span className="text-white">{r.ticks ?? "?"} ticks</span>
                  <span className="text-vts-silver/60">
                    {r.profit > 0 ? `${Number(r.mult).toFixed(2)}x` : `↑${Number(r.peak ?? r.mult ?? 1).toFixed(2)}x then spiked`}
                  </span>
                  {r.at > 0 && <span className="text-vts-silver/40 text-[9px] hidden sm:inline">{new Date(r.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
                  <span className="ml-auto font-bold">{r.profit > 0 ? "+" : ""}{Number(r.profit).toFixed(2)} {currency}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* HOW TO FLY */}
      <div data-testid="heli-instructions" className="vts-card p-4">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold mb-2">How to fly — 60 second guide</div>
        <ol className="space-y-1.5 text-[11px] text-vts-silver/75 leading-snug list-none">
          <li><b className="text-white">1. Pick your growth rate</b> — your stake compounds by this % every market tick. 1% = slow &amp; safe (long flights), 5% = fast &amp; risky (short flights, tighter safe zone).</li>
          <li><b className="text-white">2. Set your stake</b> and tap <b className="text-sky-300">LIFT OFF</b> — the rocket climbs while the price stays inside the safe zone.</li>
          <li><b className="text-white">3. Cash out before the crash</b> — tap <b className="text-amber-300">CASH OUT</b> anytime to bank the multiplier. One tick outside the zone = rocket down, stake gone.</li>
          <li><b className="text-white">4. Or set Auto cash-out</b> (e.g. 1.5x) — the platform banks it for you the moment the target is hit. No greed, no missed exits.</li>
          <li><b className="text-white">5. Autopilot</b> = hands-free sessions: it flies round after round, and every time it reaches your bank target it cashes the profit and restarts a fresh session automatically — only the stop-loss (or you) ends the run.</li>
        </ol>
        <p className="text-[10px] text-vts-blue/90 font-mono mt-2">💡 Pro tip: most rounds die young — modest targets (1.05–1.25x) cash out far more often than moonshots.</p>
      </div>

      {/* WHEN TO LAUNCH — best-time education */}
      <div data-testid="heli-when-to-launch" className="vts-card p-4">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold mb-2">When is the best time to run the rocket?</div>
        <div className="space-y-1.5 text-[11px] text-vts-silver/75 leading-snug">
          <div className="flex gap-2"><span className="shrink-0">🟢</span><span><b className="text-white">Launch when the Market Read says CALM SKIES.</b> Small, quiet ticks stay inside the safe zone longer — flights survive more ticks and your multiplier compounds further.</span></div>
          <div className="flex gap-2"><span className="shrink-0">🔀</span><span><b className="text-white">Choppy tape beats trending tape.</b> Look at the tick tape below: alternating ↑↓↑↓ is perfect. Long runs of the same arrow mean the price is marching toward the zone's edge — wait it out.</span></div>
          <div className="flex gap-2"><span className="shrink-0">🔴</span><span><b className="text-white">Don't launch into SPIKY.</b> Right after a volatility burst, one big tick kills the flight instantly. Wait for the verdict to cool back to 🟡 or 🟢 — it refreshes every 15 seconds.</span></div>
          <div className="flex gap-2"><span className="shrink-0">⚙️</span><span><b className="text-white">Match growth to conditions:</b> 1–2% growth = wider safe zone, long steady flights (best for autopilot). 3–5% = tighter zone, fast money but only in calm markets.</span></div>
          <div className="flex gap-2"><span className="shrink-0">🏦</span><span><b className="text-white">Best autopilot recipe:</b> calm market + 1–2% growth + auto cash-out 1.3x + small bank target (like +$1). It banks tiny wins over and over — the stop-loss is your only exit, so size it (e.g. −$20 with $3 stakes).</span></div>
          <div className="flex gap-2"><span className="shrink-0">📊</span><span><b className="text-white">Use your own history:</b> the survival stats below show the median tick count where half your flights die — cash out before that number and you win more often than you lose.</span></div>
        </div>
      </div>

      {/* MARKET READ — is now a good time to fly? */}
      {(() => {
        if (!read) return null;
        const verdict = read.ratio <= 0.9
          ? { chip: "🟢 CALM SKIES", cls: "bg-vts-profit/15 text-vts-profit border-vts-profit/40", msg: "Recent ticks are moving LESS than usual — the safe zone survives longer. Good time to launch." }
          : read.ratio <= 1.15
            ? { chip: "🟡 NORMAL", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", msg: "Tick movement is around its usual level — flights behave normally. OK to fly with modest targets." }
            : { chip: "🔴 SPIKY", cls: "bg-vts-loss/15 text-vts-loss border-vts-loss/40", msg: "Recent ticks are moving MORE than usual — breakouts are likelier. Consider waiting or lowering the growth rate." };
        return (
          <div data-testid="heli-market-read" className="vts-card p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold">Market read — {labelOf(symbol)}</span>
              <span data-testid="heli-market-verdict" className={`px-2 py-0.5 rounded-full border text-[10px] font-bold font-mono ${verdict.cls}`}>{verdict.chip}</span>
              <span className="text-[9px] text-vts-silver/40 font-mono ml-auto">live · refreshes 15s</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="text-center">
                <div className="font-mono text-base font-bold text-white">{read.tps.toFixed(1)}/s</div>
                <div className="text-[9px] text-vts-silver/50">tick speed (activity)</div>
              </div>
              <div className="text-center">
                <div className={`font-mono text-base font-bold ${read.ratio <= 0.9 ? "text-vts-profit" : read.ratio <= 1.15 ? "text-amber-300" : "text-vts-loss"}`}>
                  {Math.round(read.ratio * 100)}%
                </div>
                <div className="text-[9px] text-vts-silver/50">current movement vs normal</div>
              </div>
              <div className="text-center">
                <div className="font-mono text-base font-bold text-white">{Math.round(growth * 100)}%</div>
                <div className="text-[9px] text-vts-silver/50">your growth (higher = tighter zone)</div>
              </div>
            </div>
            <p className="text-[9px] text-vts-silver/45 font-mono">{verdict.msg} Based on the last 300 live ticks (latest 50 vs the 250 before). Best launch window = 🟢 CALM + a choppy ↑↓ tape below.</p>
          </div>
        );
      })()}

      {/* BEST PAIR SCANNER — which market to fly right now */}
      {scan && scan.length > 1 && (
        <div data-testid="heli-best-pair" className="vts-card p-4">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold">Best pair to fly right now</span>
            <span className="text-[9px] text-vts-silver/40 font-mono ml-auto">all pairs re-scanned every 30s</span>
          </div>
          <p className="text-[9px] text-vts-silver/45 font-mono mb-2">
            Ranked by calmness + chop across every market — the ⭐ pair currently gives flights the best chance to survive long.
          </p>
          <div className="space-y-1">
            {scan.slice(0, 5).map((s, i) => {
              const isBest = i === 0;
              const isCurrent = s.id === symbol;
              const dot = s.ratio <= 0.9 ? "🟢" : s.ratio <= 1.15 ? "🟡" : "🔴";
              return (
                <div key={s.id} data-testid={`best-pair-row-${s.id}`}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] font-mono ${
                    isBest ? "border-vts-profit/50 bg-vts-profit/10" : "border-vts-line/40 bg-vts-surface/30"}`}>
                  <span className="w-4 text-center shrink-0">{isBest ? "⭐" : dot}</span>
                  <span className="text-white font-semibold truncate">{labelOf(s.id)}</span>
                  <span className="text-vts-silver/55 hidden sm:inline">calm {Math.round(s.ratio * 100)}%</span>
                  <span className="text-vts-silver/55 hidden sm:inline">· chop {s.chop}%</span>
                  {isCurrent ? (
                    <span className="ml-auto text-[9px] px-2 py-0.5 rounded bg-vts-blue/15 text-vts-blue font-bold shrink-0">SELECTED</span>
                  ) : (
                    <button data-testid={`fly-pair-${s.id}`} onClick={() => setSymbol(s.id)} disabled={flying}
                      className="ml-auto text-[9px] px-2 py-0.5 rounded bg-vts-blue text-white font-bold hover:bg-vts-blue/80 disabled:opacity-40 shrink-0">
                      Fly this →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {bestPair && bestPair.id !== symbol && !flying && (
            <p className="text-[9px] text-amber-300/90 font-mono mt-2">💡 {labelOf(bestPair.id)} looks better than your selected pair right now — tap "Fly this" to switch.</p>
          )}
        </div>
      )}

      {/* ACCU HISTORY — previous rounds + live tick tape */}
      <div data-testid="heli-accu-history" className="vts-card p-4 space-y-2.5">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold">ACCU history</div>
        <div>
          <div className="text-[9px] text-vts-silver/45 font-mono mb-1">Previous rounds — ticks survived · cash-out multiplier</div>
          {rounds.length === 0 ? (
            <div className="text-[10px] text-vts-silver/40 font-mono">no rounds yet on this account — take your first flight</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {rounds.slice(0, 15).map((r, i) => (
                <span key={i} data-testid={`accu-round-${i}`}
                  className={`px-2 py-1 rounded-md border text-[10px] font-bold font-mono ${
                    r.profit > 0 ? "border-vts-profit/40 bg-vts-profit/10 text-vts-profit" : "border-vts-loss/40 bg-vts-loss/10 text-vts-loss"}`}>
                  {r.ticks}t {r.profit > 0 ? `· ${Number(r.mult).toFixed(2)}x` : `↑${Number(r.peak ?? r.mult ?? 1).toFixed(2)}x 💥`}
                </span>
              ))}
            </div>
          )}
        </div>
        {read?.tape?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[9px] text-vts-silver/45 font-mono">Live tick tape — last {read.tape.length} ticks ({labelOf(symbol)})</span>
              <span data-testid="heli-chop-score" className={`px-1.5 py-0.5 rounded text-[9px] font-bold font-mono ${
                read.chop >= 50 ? "bg-vts-profit/15 text-vts-profit" : read.chop >= 35 ? "bg-amber-500/15 text-amber-300" : "bg-vts-loss/15 text-vts-loss"}`}>
                chop score {read.chop}% — {read.chop >= 50 ? "GOOD ✓" : read.chop >= 35 ? "OKAY" : "RISKY — trending"}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {read.tape.map((t, i) => (
                <span key={i} className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold ${
                  t.flat ? "bg-vts-surface text-vts-silver/50" : t.up ? "bg-vts-profit/15 text-vts-profit" : "bg-vts-loss/15 text-vts-loss"}`}>
                  {t.flat ? "·" : t.up ? "↑" : "↓"}
                </span>
              ))}
            </div>
            <div className="mt-1.5 rounded-md bg-vts-surface/40 border border-vts-line/30 px-2 py-1.5 space-y-0.5">
              <p className="text-[9px] text-vts-silver/60 font-mono">📖 <b className="text-white">How to read this:</b> each square = one market tick. <span className="text-vts-profit">↑ price went up</span> · <span className="text-vts-loss">↓ price went down</span>.</p>
              <p className="text-[9px] text-vts-silver/60 font-mono">✅ <b className="text-white">Good:</b> arrows keep flipping ↑↓↑↓ — the price is bouncing in place, so the safe zone survives. That's a high chop score.</p>
              <p className="text-[9px] text-vts-silver/60 font-mono">⚠️ <b className="text-white">Bad:</b> many same arrows in a row (↑↑↑↑ or ↓↓↓↓) — the price is marching one way and will walk out of the safe zone. Wait for the flipping to return.</p>
            </div>
          </div>
        )}
      </div>

      {/* SURVIVAL STATS */}
      {rounds.length >= 3 && (() => {
        const tickList = rounds.map((r) => r.ticks).sort((a, b) => a - b);
        const avgT = tickList.reduce((s, t) => s + t, 0) / tickList.length;
        const medT = tickList[Math.floor(tickList.length / 2)];
        const crashes = rounds.filter((r) => r.profit <= 0).length;
        return (
          <div data-testid="heli-survival-stats" className="vts-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold mb-2">Your survival stats — read the sky</div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="text-center"><div className="font-mono text-base font-bold text-white">{avgT.toFixed(0)}t</div><div className="text-[9px] text-vts-silver/50">avg ticks / round</div></div>
              <div className="text-center"><div className="font-mono text-base font-bold text-white">{medT}t</div><div className="text-[9px] text-vts-silver/50">median (half die before this)</div></div>
              <div className="text-center"><div className={`font-mono text-base font-bold ${crashes / rounds.length > 0.2 ? "text-vts-loss" : "text-amber-300"}`}>{Math.round((crashes / rounds.length) * 100)}%</div><div className="text-[9px] text-vts-silver/50">breakouts (crashes)</div></div>
            </div>
            <p className="text-[9px] text-vts-silver/45 font-mono">
              Median {medT} ticks means half your flights crash before {medT} ticks — cashing out around {Math.max(5, Math.round(medT * 0.6))} ticks wins more often than holding.
            </p>
          </div>
        );
      })()}

      <p className="text-[9px] text-vts-silver/45 font-mono">
        Accumulator contracts: your stake compounds {Math.round(growth * 100)}% per tick while the price stays inside the safe zone.
        One tick outside = the full stake is lost. High risk — fly with money you can afford to lose.
      </p>
    </div>
  );
};

export default HeliRide;
